import { useTranslation } from "react-i18next";
import { Select } from "../../../components/control/Select";
import { TextArea } from "../../../components/control/TextArea";
import { TextInput } from "../../../components/control/TextInput";
import { DraftActions } from "../../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../../drafts/useWorkspaceDraft";
import { useBookDetail } from "../bookContext";
export function BookMetadataForm({
  onClose,
  inline = false,
}: {
  onClose?: (saved: boolean) => void;
  inline?: boolean;
}) {
  const book = useBookDetail();
  const { t } = useTranslation(["overview", "common", "nav"]);
  const extracted = book.metadataExtraction;
  const embedded = extracted?.embedded as { title?: string; authorName?: string } | undefined;
  const canSuggest = !book.metadataConfirmedAt;
  const initial = {
    title:
      canSuggest &&
      typeof extracted?.title === "string" &&
      (!book.title || book.title === embedded?.title)
        ? extracted.title
        : (book.title ?? ""),
    authorName: (canSuggest &&
    typeof extracted?.authorName === "string" &&
    (!book.authorName || book.authorName === embedded?.authorName)
      ? extracted.authorName
      : (book.authorName ?? "")) as string | null,
    language: book.language ?? book.languageDetected ?? "de",
  };
  const draft = useWorkspaceDraft(book.id, "metadata", initial);
  const value = draft.value;
  const update = (patch: Partial<typeof initial>) =>
    draft.setValue((old) => ({ ...old, ...patch }));
  const busy = draft.applying || !draft.loaded;
  const evidence = Array.isArray(extracted?.evidence)
    ? (extracted.evidence as { field: string; value: string; nodeIds: string[] }[])
    : [];
  const languageOptions = [
    { value: "de", label: t("common:bookLanguage.de") },
    { value: "en", label: t("common:bookLanguage.en") },
  ];
  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="space-y-4"
      onBlur={() => void draft.flush().catch(() => {})}
    >
      <p className="text-ink-2">{t("nav:workspace.metadataHelp")}</p>
      {extracted?.status === "pending" ? (
        <p role="status">{t("nav:workspace.extracting")}</p>
      ) : null}
      <TextInput
        label={t("overview:edit.title")}
        value={value.title}
        maxLength={300}
        disabled={busy}
        onChange={(e) => update({ title: e.target.value })}
      />
      <TextArea
        label={t("overview:book.author")}
        value={value.authorName ?? ""}
        rows={3}
        maxLength={4000}
        disabled={busy}
        onChange={(e) => update({ authorName: e.target.value })}
      />
      {!value.authorName && !book.metadataConfirmedAt ? (
        <label className="flex items-start gap-2 text-data">
          <input
            type="checkbox"
            checked={value.authorName === null}
            disabled={busy}
            onChange={(e) => update({ authorName: e.target.checked ? null : "" })}
          />
          <span>{t("nav:workspace.unattributed")}</span>
        </label>
      ) : null}
      <Select
        label={t("overview:book.language")}
        value={value.language}
        disabled={busy || !!book.confirmedAt}
        hint={book.confirmedAt ? t("overview:edit.languageLocked") : undefined}
        options={languageOptions}
        onChange={(e) => update({ language: e.target.value === "en" ? "en" : "de" })}
      />
      {canSuggest && evidence.length ? (
        <div className="border-l-2 border-line pl-3 text-data text-ink-2">
          <p className="font-medium">{t("nav:workspace.sourceText")}</p>
          {evidence.map((e) => (
            <p key={`${e.field}-${e.value}`}>{e.value}</p>
          ))}
        </div>
      ) : null}
      {canSuggest && embedded && (embedded.title || embedded.authorName) ? (
        <div className="border-l-2 border-line pl-3 text-data text-ink-2">
          <p className="font-medium">{t("nav:workspace.sourceMetadata")}</p>
          <p>{embedded.title}</p>
          <p className="whitespace-pre-wrap break-words">{embedded.authorName}</p>
        </div>
      ) : null}
      <DraftActions
        inline={inline}
        draft={draft}
        always={!book.metadataConfirmedAt}
        disabled={
          !value.title.trim() ||
          !["de", "en"].includes(value.language) ||
          (!book.metadataConfirmedAt && value.authorName !== null && !value.authorName.trim())
        }
        label={
          book.metadataConfirmedAt ? t("nav:workspace.apply") : t("nav:workspace.confirmMetadata")
        }
        onApply={() =>
          void draft
            .apply()
            .then(() => onClose?.(true))
            .catch(() => {})
        }
      />
    </form>
  );
}
